import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// --- ICONS (using inline SVGs for portability) ---
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const BriefcaseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;
const ShieldCheckIcon = ({ className = "w-5 h-5" }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>;
const BrainCircuitIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-purple-400"><path d="M12 5a3 3 0 1 0-5.993 1.003c.005-.002.01-.003.015-.003H12V5Z" /><path d="M12 19a3 3 0 1 1 5.99-1.004A2.981 2.981 0 0 1 18 18h-5.978" /><path d="M14.5 13a3 3 0 1 0-2.48 2.993c.003.001.005.002.008.002H14.5v-3Z" /><path d="M9.5 13a3 3 0 1 1 2.48-2.993A2.98 2.98 0 0 1 12 10h-2.5v3Z" /><path d="M18 5a3 3 0 1 1-5.993-1.003C12.007 3.998 12.013 4 12.02 4H18v1Z" /><path d="M6 19a3 3 0 1 0 5.993-1.003C11.995 18.002 11.99 18 11.985 18H6v1Z" /><path d="M6 5a3 3 0 1 0 0 6 3 3 0 1 0 0-6Z" /><path d="M18 13a3 3 0 1 0 0 6 3 3 0 1 0 0-6Z" /></svg>;
const AlertTriangleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-yellow-400"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>;
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-green-400"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;
const XCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-red-400"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>;
const SpinnerIcon = () => <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;


// --- CORRECTED DATA CONSTANTS ---
const departments = [
    { id: 'a1b2c3d4-e5f6-7890-1234-567890abcdef', name: 'Information Technology' },
    { id: 'b2c3d4e5-f6a7-8901-2345-67890abcdef1', name: 'Human Resources' },
    { id: 'c3d4e5f6-a7b8-9012-3456-7890abcdef12', name: 'Finance' },
    { id: 'd4e5f6a7-b8c9-0123-4567-890abcdef123', name: 'Executive Management' },
    { id: 'e5f6a7b8-c9d0-1234-5678-90abcdef1234', name: 'Operations' },
];

const roles = [
    { id: 1, name: 'EndUser', description: 'I use company systems for daily tasks.' },
    { id: 2, name: 'Management', description: 'I lead teams and manage business risks.' },
    { id: 3, name: 'SystemAdmin', description: 'I manage and secure IT infrastructure.' },
];

const assessmentQuestions = [
    { id: 1, type: 'threat', text: 'An email from "IT Servlces" asks you to validate your credentials due to a server upgrade. The link points to "microsft-login.com".', skill: 'Phishing Awareness' },
    { id: 2, type: 'safe', text: 'A calendar invite from your manager for a scheduled team meeting.', skill: 'General Awareness' },
    { id: 3, type: 'threat', text: 'A USB drive you found in the parking lot labeled "Q3 Salaries".', skill: 'Physical Security' },
    { id: 4, type: 'safe', text: 'An internal system prompts you for your MFA code after you enter your password.', skill: 'Password Security' },
    { id: 5, type: 'threat', text: 'A website offers a free download of a popular paid software.', skill: 'Malware Defense' },
];

// --- NEW: AWARENESS ANALYSIS LOGIC ---
/**
 * Analyzes the user's assessment results to determine their cybersecurity awareness level.
 * @param {Array} results - The array of answers from the assessment game.
 * @returns {Object} An analysis object with overall score, level, and skill breakdown.
 */
const analyzeCybersecurityAwareness = (results) => {
    if (!results || results.length === 0) {
        return {
            overallScore: 0,
            awarenessLevel: 'Undetermined',
            skillBreakdown: {},
            strengths: [],
            weaknesses: [],
        };
    }

    const totalQuestions = results.length;
    const correctAnswers = results.filter(r => r.isCorrect).length;
    const overallScore = (correctAnswers / totalQuestions) * 100;

    const getLevel = (score) => {
        if (score >= 80) return 'High';
        if (score >= 50) return 'Medium';
        return 'Low';
    };

    const awarenessLevel = getLevel(overallScore);

    const skillBreakdown = results.reduce((acc, result) => {
        if (!acc[result.skill]) {
            acc[result.skill] = { correct: 0, total: 0 };
        }
        acc[result.skill].total += 1;
        if (result.isCorrect) {
            acc[result.skill].correct += 1;
        }
        return acc;
    }, {});

    const strengths = [];
    const weaknesses = [];

    for (const skill in skillBreakdown) {
        const score = (skillBreakdown[skill].correct / skillBreakdown[skill].total) * 100;
        skillBreakdown[skill].score = score;
        const level = getLevel(score);
        skillBreakdown[skill].level = level;

        if (level === 'High') strengths.push(skill);
        if (level === 'Low') weaknesses.push(skill);
    }

    return {
        overallScore,
        awarenessLevel,
        skillBreakdown,
        strengths,
        weaknesses,
    };
};


// --- MAIN COMPONENT ---
export default function RegisterComponent({ onRegisterSubmit }) {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        department: '',
        role: '',
    });
    const [assessmentResults, setAssessmentResults] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const nextStep = () => setStep(prev => prev + 1);
    const prevStep = () => setStep(prev => prev - 1);

    const handleFormChange = (e) => {
        setError('');
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (name, value) => {
        setError('');
        setFormData({ ...formData, [name]: value });
    };

    const handleAssessmentComplete = (results) => {
        setAssessmentResults(results);
        nextStep();
    };

    const handleFinalSubmit = async () => {
        setIsSubmitting(true);
        setError('');

        // --- MODIFIED: Analyze results and add to payload ---
        const awarenessAnalysis = analyzeCybersecurityAwareness(assessmentResults);

        // Create a new payload object with keys that match the backend API
        const payload = {
            user_data: {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                password: formData.password,
                department_id: formData.department, // The state now holds the correct UUID
                role_id: formData.role,             // The state now holds the correct integer ID
            },
            assessment_data: awarenessAnalysis, // Add the analysis results
        };

        const result = await onRegisterSubmit(payload);

        if (result && !result.success) {
            setError(result.message || "An unknown error occurred.");
            setStep(1);
        }
        setIsSubmitting(false);
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return <Step1_UserInfo formData={formData} handleChange={handleFormChange} nextStep={nextStep} />;
            case 2:
                return <Step2_RoleInfo formData={formData} handleSelect={handleSelectChange} nextStep={nextStep} prevStep={prevStep} />;
            case 3:
                return <Step3_Briefing nextStep={nextStep} />;
            case 4:
                return <Step4_ThreatTriageGame onComplete={handleAssessmentComplete} />;
            case 5:
                return <Step5_Complete name={formData.firstName} onProceed={handleFinalSubmit} isSubmitting={isSubmitting} />;
            default:
                return <Step1_UserInfo formData={formData} handleChange={handleFormChange} nextStep={nextStep} />;
        }
    };

    return (
        <div className="text-white font-sans min-h-screen relative">
            <div className="fixed inset-0" style={{ backgroundImage: "url('/assets/images/levels_bg.png')", backgroundSize: 'cover', backgroundPosition: 'center', zIndex: -1 }}></div>
            <div className="fixed inset-0 bg-black/60" style={{ zIndex: -1 }}></div>
            <div className="min-h-screen flex flex-col items-center justify-center p-3 sm:p-4">
                <img
                    src={'/assets/images/logo.png'}
                    alt="CyberJugs Logo"
                    className="w-24 h-24 sm:w-38 sm:h-38 cursor-pointer transition-all inline-block"
                />
                <div className="w-full max-w-2xl bg-neon-600/80 backdrop-blur-sm shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] text-white font-bold rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 disabled:bg-gray-600 disabled:cursor-not-allowed">
                    <div className="p-4 sm:p-6">
                        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 text-center p-3 rounded-lg mb-6">{error}</div>}
                        {renderStep()}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- STEP COMPONENTS (Unchanged) ---

const Step1_UserInfo = ({ formData, handleChange, nextStep }) => {
    const isFormValid = formData.firstName && formData.lastName && formData.email && formData.password.length >= 8;
    return (
        <div className="animate-fade-in">
            <h2 className="text-xl sm:text-2xl font-semibold text-center mb-6">Create Your Profile</h2>
            <form onSubmit={(e) => { e.preventDefault(); if (isFormValid) nextStep(); }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-400" />
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-400" />
                </div>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" required className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-400" />
                <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Password (min. 8 characters)" required className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-400" />
                <button type="submit" disabled={!isFormValid} className="w-full bg-neon-600/80 backdrop-blur-sm shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 hover:shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] disabled:bg-gray-600 disabled:cursor-not-allowed">
                    Next: Define Role
                </button>
            </form>
        </div>
    );
};

const Step2_RoleInfo = ({ formData, handleSelect, nextStep, prevStep }) => {
    return (
        <div className="animate-fade-in">
            <h2 className="text-xl sm:text-2xl font-semibold text-center">Define Your Operational Role</h2>
            <div className="space-y-6">
                <div>
                    <label className="block text-gray-400 mb-2">Select Your Department</label>
                    <select name="department" value={formData.department} onChange={(e) => handleSelect('department', e.target.value)} required className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-black">
                        <option value="" disabled>-- Select Department --</option>
                        {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-gray-400 mb-2">Select Your Primary Role</label>
                    <div className="space-y-3">
                        {roles.map(role => (
                            <button key={role.id} onClick={() => handleSelect('role', role.id)} className={`w-full text-left p-4 border rounded-lg transition-all duration-200 flex items-center space-x-4 ${formData.role === role.id ? 'bg-blue-600/30 border-blue-500 ring-2 ring-blue-500' : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.1)]'}`}>
                                <div className="flex-shrink-0">{role.name === 'EndUser' ? <UserIcon /> : role.name === 'Management' ? <BriefcaseIcon /> : <ShieldCheckIcon />}</div>
                                <div>
                                    <p className="font-semibold">{role.name}</p>
                                    <p className="text-sm text-gray-400">{role.description}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-8">
                <button onClick={prevStep} className="w-full sm:w-auto bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.1)] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">Back</button>
                <button onClick={nextStep} disabled={!formData.department || !formData.role} className="w-full sm:w-auto bg-neon-600/80 backdrop-blur-sm shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 hover:shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] disabled:bg-gray-600 disabled:cursor-not-allowed">

                    Proceed to Assessment
                </button>
            </div>
        </div>
    );
};

const Step3_Briefing = ({ nextStep }) => {
    return (
        <div className="animate-fade-in text-center">
            <div className="flex justify-center mb-6">
                <BrainCircuitIcon />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold mb-4">Cyber Aptitude Assessment Protocol</h2>
            <p className="text-gray-400 mb-6">
                To tailor your training path, we must first establish a baseline of your current cyber instincts.
                You will be presented with a series of rapid-fire scenarios. Your task is to triage each item as a potential <span className="text-red-400 font-bold">'Threat'</span> or <span className="text-green-400 font-bold">'Safe'</span>.
            </p>
            <p className="text-yellow-400 font-semibold mb-8">Accuracy and speed are key. There are no penalties for wrong answers. Respond with your first instinct.</p>
            <button onClick={nextStep} className="w-full bg-neon-600/80 backdrop-blur-sm shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 hover:shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] disabled:bg-gray-600 disabled:cursor-not-allowed">
                Begin Assessment
            </button>
        </div>
    );
};

const Step4_ThreatTriageGame = ({ onComplete }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [feedback, setFeedback] = useState(null);

    const currentQuestion = assessmentQuestions[currentIndex];

    const handleAnswer = (answer) => {
        const isCorrect = (answer === 'threat' && currentQuestion.type === 'threat') || (answer === 'safe' && currentQuestion.type === 'safe');

        setFeedback(isCorrect ? 'correct' : 'incorrect');

        const newAnswers = [...answers, { questionId: currentQuestion.id, answer, isCorrect, skill: currentQuestion.skill }];
        setAnswers(newAnswers);

        setTimeout(() => {
            setFeedback(null);
            if (currentIndex < assessmentQuestions.length - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                onComplete(newAnswers);
            }
        }, 800);
    };

    const progressPercentage = ((currentIndex + 1) / assessmentQuestions.length) * 100;

    return (
        <div className="animate-fade-in">
            <div className="mb-4">
                <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progressPercentage}%` }}></div>
                </div>
                <p className="text-center text-sm text-gray-400 mt-2">Scenario {currentIndex + 1} of {assessmentQuestions.length}</p>
            </div>

            <div className={`p-4 sm:p-6 bg-black/40 border border-[rgba(255,255,255,0.1)] rounded-lg min-h-[150px] flex items-center justify-center text-center transition-all duration-300 ${feedback === 'correct' ? 'ring-2 ring-green-500' : feedback === 'incorrect' ? 'ring-2 ring-red-500' : ''}`}>
                {feedback ? (
                    <div className="animate-fade-in">
                        {feedback === 'correct' ? <CheckCircleIcon /> : <XCircleIcon />}
                    </div>
                ) : (
                    <p className="text-lg">{currentQuestion.text}</p>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-6">
                <button onClick={() => handleAnswer('threat')} disabled={!!feedback} className="bg-gray-600/80 backdrop-blur-sm border border-red-400/50 hover:shadow-[0_0_10px_#f00,0_0_10px_#f00,0_0_10px_#f00] hover:scale-105 disabled:opacity-50 text-white font-bold py-4 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2">
                    <AlertTriangleIcon /><span>Threat</span>
                </button>
                <button onClick={() => handleAnswer('safe')} disabled={!!feedback} className="bg-gray-600/80 backdrop-blur-sm border border-green-400/50 hover:shadow-[0_0_10px_#0f0,0_0_10px_#0f0,0_0_10px_#0f0] hover:scale-105 disabled:opacity-50 text-white font-bold py-4 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2">
                    <CheckCircleIcon /><span>Safe</span>
                </button>
            </div>
        </div>
    );
};

const Step5_Complete = ({ name, onProceed, isSubmitting }) => {
    return (
        <div className="animate-fade-in text-center p-4 sm:p-8">
            <div className="flex justify-center mb-6">
                <ShieldCheckIcon className="w-16 h-16 text-green-400" />
            </div>
            <h2 className="text-3xl font-bold text-green-400 mt-4 mb-2 ">Onboarding Complete</h2>
            <p className="text-xl text-gray-300 mb-6">Welcome to the program, Operator <span className="text-purple-400 font-semibold">{name}</span>.</p>
            <p className="text-gray-400 mb-8">Your baseline aptitude has been established. Click below to complete your registration and proceed to the login page.</p>
            <div className="bg-blue-900/30 border border-blue-800 rounded-md p-4 mb-6">
                <p className="text-blue-200 text-sm">
                    <strong>Important:</strong> After registration, your account will require administrator approval before you can log in. You will be notified when your account is activated.
                </p>
            </div>
            <button
                onClick={onProceed}
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 text-lg shadow-lg shadow-blue-600/20 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center">
                {isSubmitting ? <SpinnerIcon /> : 'Complete Registration & Go to Login'}
            </button>
        </div>
    );
};